import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@/app/components/common/app-select/AppSelect";
import React, {useMemo} from "react";
import {MainCtaBtn} from "@/app/components/common/main-cta-btn/MainCtaBtn";
import {IBalance} from "@/lib/types/wallet/IBalance";
import {useTranslations} from "next-intl";
import Decimal from "decimal.js";
import {useAppDispatch, useAppSelector} from "@/lib/hooks";
import {requestCurrencySwitch, setUserCurrency} from "@/lib/slices/cashierSlice";
import {ChevronDownSvg} from "@/app/components/icons/ChevronDownSvg";
import ProgressBar from "@/app/components/bonuses/ProgressBar";
import {useGetBonusesQuery} from "@/lib/api/wallet/bonusApi";
import {WelcomeBonusStatusesEnum} from "@/lib/enums/WelcomeBonusStatusesEnum";
import {Link, usePathname} from "@/i18n/navigation";
import {toPageName} from "@/lib/analytics/page-name";
import {trackButtonClicked} from "@/lib/analytics/mixpanel-events";
import {useMoney} from "@/lib/utils/money/useMoney";
import {isDisplayCurrencyEnabled} from "@/lib/utils/contants/displayCurrencies";

interface IProps {
    onWalletClick: () => void;
    onGoToBonusesClick: () => void;
    balances: IBalance[];
}

export default function BalancePopoverContent({onWalletClick, balances, onGoToBonusesClick}: IProps) {
    const t = useTranslations();
    const currency = useAppSelector(state => state.cashier.userCurrency);
    const activeGameCurrency = useAppSelector(state => state.cashier.activeGameCurrency);
    const dispatch = useAppDispatch();
    const {data: bonuses} = useGetBonusesQuery();
    const pathname = usePathname();

    // The bonus-balance row is a link into the bonuses page, opened from the
    // header's balance popover.
    const onBonusesLinkClick = () => {
        trackButtonClicked({
            buttonName: 'open_bonuses',
            component: 'header',
            page: toPageName(pathname),
        });
        onGoToBonusesClick();
    }

    const {fm} = useMoney();

    const balance = useMemo(() => {
        // Stale-cookie guard: unknown display currency falls back to USD.
        return balances?.find(i => i.currency === (currency || 'USD'))
            ?? balances?.find(i => i.currency === 'USD');
    }, [currency, balances]);

    // Values from the balance array are ALREADY in the selected currency —
    // fm() only formats, no conversion (AC-798).
    const balanceValue = useMemo(() => {
        return fm(balance?.realBalance || 0, balance?.currency)
    }, [fm, balance?.realBalance, balance?.currency])

    const bonusBalanceValue = useMemo(() => {
        return fm(balance?.bonusBalance || 0, balance?.currency)
    }, [fm, balance?.bonusBalance, balance?.currency])

    const bonusWageringProgressValue = useMemo(() => {
        return fm(balance?.bonusWageringProgress || 0, balance?.currency)
    }, [fm, balance?.bonusWageringProgress, balance?.currency])

    const bonusWageringRequirementValue = useMemo(() => {
        return fm(balance?.bonusWageringRequirement || 0, balance?.currency)
    }, [fm, balance?.bonusWageringRequirement, balance?.currency])

    const isShowProgress = useMemo(() => {
        return bonuses?.some(i => i.status === WelcomeBonusStatusesEnum.ACTIVE)
            && new Decimal(balance?.bonusWageringProgress || 0).lt(balance?.bonusWageringRequirement || 0);
    }, [balance?.bonusWageringProgress, balance?.bonusWageringRequirement, bonuses])

    const onCurrencyChange = (newCurrency: string) => {
        // AC-798: with a game open, the session currency is fixed at launch —
        // switching the display currency silently would put the header and the
        // in-game balance in two different currencies. Hand the request to the
        // game page, which asks the player to confirm a game restart. The one
        // exception: picking the currency the game already runs in (e.g. after
        // a USD launch fallback) is a pure display change and applies directly.
        if (activeGameCurrency && newCurrency !== activeGameCurrency) {
            dispatch(requestCurrencySwitch(newCurrency));
            return;
        }
        dispatch(setUserCurrency(newCurrency))
    }

    const progress = useMemo(() => {
        if (!balance?.bonusWageringProgress || !balance?.bonusWageringRequirement) return 0;
        return (balance?.bonusWageringProgress * 100 / balance?.bonusWageringRequirement);
    }, [balance?.bonusWageringProgress, balance?.bonusWageringRequirement])

    return (
        <div>
            <div className={'flex justify-between items-center mb-[16px]'}>
                <div>
                    <span
                        className={'text-light-gray font-medium leading-none text-[14px] mb-[4px] block'}>{t('BalancePopover.balance')}</span>
                    <span
                        className={'text-noble-black-0 leading-none font-semibold'}>{balanceValue}</span>
                </div>
                <div>
                    <Select value={currency || 'USD'} onValueChange={onCurrencyChange}>
                        <SelectTrigger className={'px-[11px] py-[10px] border-2'}>
                            <SelectValue/>
                        </SelectTrigger>
                        <SelectContent className={'z-4 max-h-[300px]'}>
                            {balances.filter(i => isDisplayCurrencyEnabled(i.currency)).map((i) => (
                                <SelectItem key={i.currency} value={i.currency}>
                                    {i.currency}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <Link onClick={onBonusesLinkClick} prefetch={false} href={'/casino/bonuses'} className={'flex items-center justify-between mb-[16px]'}>
                <span className={'block'}>
                    <span
                        className={'text-light-gray font-medium leading-none text-[14px] mb-[4px] block'}>{t('BalancePopover.bonusBalance')}</span>
                    <span
                        className={'text-noble-black-0 leading-none font-semibold'}>{bonusBalanceValue}</span>
                </span>
                <ChevronDownSvg className={'rotate-[-90deg] text-noble-black-300'}/>
            </Link>

            {
                isShowProgress ? (
                    <>
                        <ProgressBar progress={progress} className={'mb-[6px]'}/>
                        <div
                            className={'text-white-transparent-40 flex items-center justify-between uppercase text-[12px] mb-[16px]'}>
                            <div className={'font-medium leading-[18px]'}>
                                <span className={'mr-1'}>{t('BalancePopover.from')}</span>
                                <span>{bonusWageringProgressValue}</span>
                            </div>
                            <div className={'font-medium leading-[18px]'}>
                                <span className={'mr-1'}>{t('BalancePopover.to')}</span>
                                <span>{bonusWageringRequirementValue}</span>
                            </div>
                        </div>
                    </>

                ) : null
            }

            <MainCtaBtn onClick={onWalletClick} className={'w-full h-[51px] md:text-[16px]'}>
                {t('BalancePopover.wallet')}
            </MainCtaBtn>
        </div>
    )
}
